import React, { useEffect, useState, useMemo, useRef, useCallback } from 'react'
import styles from './styles.module.css'
import Modal from './Modal'
import { createFile, deleteFiles, deleteLowFolder, getMethodOfficeFile, getMethodOfficeFiles, getMethodOfficeFolders } from '../../actions/methodOffice'
import { useDispatch, useSelector } from 'react-redux'
import { API_BASE_URL } from '../../assets/const'
import { createNotification } from '../../components/Notifications/Notifications'
import cn from 'classnames'
import MediaPreviewModal from './MediaPreviewModal'

export default function MethodOffice() {
    const dispatch = useDispatch()
    const fileInputRef = useRef(null)
    const contextMenuRef = useRef(null)

    const [selectedFolder, setSelectedFolder] = useState(null)
    const [selectedFile, setSelectedFile] = useState(null)
    const [modalData, setModalData] = useState(null)
    const [expandedFolders, setExpandedFolders] = useState(new Set())
    const [folderTreeKey, setFolderTreeKey] = useState(0)
    const [previewFile, setPreviewFile] = useState(null)
    const [previewObjectUrl, setPreviewObjectUrl] = useState(null)
    const [contextMenu, setContextMenu] = useState(null)
    const [deleteConfirm, setDeleteConfirm] = useState(null)
    const [searchQuery, setSearchQuery] = useState('')
    const [treeFilesMap, setTreeFilesMap] = useState({})
    const [folderHistory, setFolderHistory] = useState([])
    const fetchedFolderIds = useRef(new Set())

    const { folder, files, subfolder, loading } = useSelector(state => state.method_office)
    const authToken = useSelector(state => state.auth.token)

    // Keep treeFilesMap in sync with Redux files state for the selected folder
    useEffect(() => {
        if (selectedFolder && files) {
            setTreeFilesMap(prev => ({ ...prev, [selectedFolder.mrid]: files }))
        }
    }, [files, selectedFolder?.mrid])

    const selectedFolderContent = useMemo(() => {
        if (!selectedFolder) return { childFolders: [], files: [] }
        let childFolders = []
        if (subfolder && subfolder.length > 0) {
            childFolders = subfolder.filter(f => f.mr_isfile !== 1)
        } else if (folder) {
            childFolders = folder.filter(f => f.mr_parent === selectedFolder.mrid && f.mr_isfile !== 1)
        }
        return { childFolders, files: files || [] }
    }, [selectedFolder, folder, subfolder, files])

    const buildFolderTree = (parentId, allFolders) => {
        const children = allFolders.filter(f => f.mr_parent === parentId && f.mr_isfile !== 1)
        if (children.length === 0) return []
        return children.map(child => ({ ...child, children: buildFolderTree(child.mrid, allFolders) }))
    }

    const folderTree = useMemo(() => {
        if (!folder || folder.length === 0) return []
        return buildFolderTree(0, folder)
    }, [folder, folderTreeKey])

    const getAllFolderIds = useCallback((tree) => {
        const ids = []
        const walk = (nodes) => nodes.forEach(n => { ids.push(n.mrid); if (n.children?.length) walk(n.children) })
        walk(tree)
        return ids
    }, [])

    const allFolderIds = useMemo(() => getAllFolderIds(folderTree), [folderTree, getAllFolderIds])

    const isFullyExpanded = useMemo(() => {
        if (allFolderIds.length === 0) return false
        return allFolderIds.every(id => expandedFolders.has(id))
    }, [allFolderIds, expandedFolders])

    useEffect(() => {
        dispatch(getMethodOfficeFolders(0))
    }, [dispatch])

    useEffect(() => {
        if (selectedFolder?.mrid) {
            Promise.all([
                dispatch(getMethodOfficeFolders(selectedFolder.mrid)),
                dispatch(getMethodOfficeFiles(selectedFolder.mrid))
            ]).catch(() => createNotification('error', 'Ошибка загрузки данных'))
        }
    }, [selectedFolder?.mrid, dispatch])

    useEffect(() => {
        if (previewFile) {
            dispatch(getMethodOfficeFile({ mrid: previewFile.mrid, mr_name: previewFile.mr_name }))
                .then(url => {
                    if (url) setPreviewObjectUrl(url)
                })
        } else {
            if (previewObjectUrl) {
                URL.revokeObjectURL(previewObjectUrl)
                setPreviewObjectUrl(null)
            }
        }
    }, [previewFile])

    const fetchTreeFiles = useCallback(async (folderId) => {
        if (!authToken || fetchedFolderIds.current.has(folderId)) return
        fetchedFolderIds.current.add(folderId)
        try {
            const response = await fetch(`${API_BASE_URL}/mathodicalroom/files/${folderId}`, {
                method: 'GET',
                headers: {
                    'Access-Control-Allow-Origin': '*',
                    'Content-Type': 'application/json',
                    Authorization: `Basic ${btoa(`nnz:${authToken}`)}`
                }
            })
            if (response.ok) {
                const data = await response.json()
                setTreeFilesMap(prev => ({ ...prev, [folderId]: data }))
            } else {
                fetchedFolderIds.current.delete(folderId)
            }
        } catch (err) {
            fetchedFolderIds.current.delete(folderId)
            console.error('Error fetching tree files:', err)
        }
    }, [authToken])

    const toggleFolder = useCallback((folderId, e) => {
        if (e) e.stopPropagation()
        setExpandedFolders(prev => {
            const next = new Set(prev)
            if (next.has(folderId)) {
                next.delete(folderId)
            } else {
                next.add(folderId)
                fetchTreeFiles(folderId)
            }
            return next
        })
    }, [fetchTreeFiles])

    const expandAll = useCallback(() => {
        const ids = getAllFolderIds(folderTree)
        setExpandedFolders(new Set(ids))
        ids.forEach(id => fetchTreeFiles(id))
    }, [folderTree, getAllFolderIds, fetchTreeFiles])

    const collapseAll = useCallback(() => setExpandedFolders(new Set()), [])

    const openModal = (data = null) => setModalData(data || {})

    const closeModal = (shouldRefresh = false) => {
        setModalData(null)
        if (shouldRefresh) setTimeout(refreshFolderTree, 500)
    }

    const closePreview = () => setPreviewFile(null)

    const refreshFolderTree = async () => {
        try {
            await dispatch(getMethodOfficeFolders(0))
            if (selectedFolder?.mrid) {
                await Promise.all([
                    dispatch(getMethodOfficeFolders(selectedFolder.mrid)),
                    dispatch(getMethodOfficeFiles(selectedFolder.mrid))
                ])
            }
            setTimeout(() => setFolderTreeKey(prev => prev + 1), 200)
        } catch (err) {
            console.error('Error refreshing folder tree:', err)
        }
    }

    const handleFolderSelect = useCallback((folderItem) => {
        if (selectedFolder && selectedFolder.mrid !== folderItem.mrid) {
            setFolderHistory(prev => [...prev, selectedFolder])
        }
        setSelectedFolder(folderItem)
        setSelectedFile(null)
        if (folderItem.mr_parent > 0) {
            setExpandedFolders(prev => {
                const next = new Set(prev)
                next.add(folderItem.mr_parent)
                return next
            })
        }
    }, [selectedFolder])

    const handleBack = () => {
        if (folderHistory.length === 0) return
        const newHistory = [...folderHistory]
        const prev = newHistory.pop()
        setFolderHistory(newHistory)
        setSelectedFolder(prev)
        setSelectedFile(null)
    }

    const handleClose = () => {
        setSelectedFolder(null)
        setSelectedFile(null)
        setFolderHistory([])
    }

    const openContextMenu = (e, item, type) => {
        e.stopPropagation()
        e.preventDefault()
        const rect = e.currentTarget.getBoundingClientRect()
        setContextMenu({ x: rect.left, y: rect.bottom + 4, item, type })
    }

    const closeContextMenu = useCallback(() => setContextMenu(null), [])

    const handleDownloadFile = async (item) => {
        closeContextMenu()
        try {
            const objectURL = await dispatch(getMethodOfficeFile({ mrid: item.mrid, mr_name: item.mr_name }))
            if (!objectURL) {
                createNotification('error', 'Не удалось скачать файл')
                return
            }
            const link = document.createElement('a')
            link.href = objectURL
            link.download = item.mr_name
            document.body.appendChild(link)
            link.click()
            document.body.removeChild(link)
            URL.revokeObjectURL(objectURL)
        } catch {
            createNotification('error', 'Не удалось скачать файл')
        }
    }

    useEffect(() => {
        if (!contextMenu) return
        const handler = (e) => {
            if (contextMenuRef.current && !contextMenuRef.current.contains(e.target)) closeContextMenu()
        }
        const keyHandler = (e) => { if (e.key === 'Escape') closeContextMenu() }
        document.addEventListener('mousedown', handler)
        document.addEventListener('keydown', keyHandler)
        return () => {
            document.removeEventListener('mousedown', handler)
            document.removeEventListener('keydown', keyHandler)
        }
    }, [contextMenu, closeContextMenu])

    const requestDelete = (item, type) => {
        closeContextMenu()
        setDeleteConfirm({ item, type })
    }

    const confirmDelete = async () => {
        if (!deleteConfirm) return
        const { item, type } = deleteConfirm
        setDeleteConfirm(null)
        try {
            if (type === 'folder') {
                await dispatch(deleteLowFolder(item.mrid))
                if (selectedFolder?.mrid === item.mrid) {
                    setSelectedFolder(null)
                } else if (selectedFolder?.mrid) {
                    await dispatch(getMethodOfficeFolders(selectedFolder.mrid))
                }
                createNotification('success', 'Папка успешно удалена')
            } else {
                await dispatch(deleteFiles(item.mrid))
                setSelectedFile(null)
                const parentFolderId = item.mr_parent ?? selectedFolder?.mrid
                if (parentFolderId) {
                    await dispatch(getMethodOfficeFiles(parentFolderId))
                    setTreeFilesMap(prev => {
                        const updated = { ...prev }
                        if (updated[parentFolderId]) {
                            updated[parentFolderId] = updated[parentFolderId].filter(f => f.mrid !== item.mrid)
                        }
                        return updated
                    })
                }
                createNotification('success', 'Файл успешно удалён')
            }
        } catch (err) {
            createNotification('error', `Ошибка удаления ${type === 'folder' ? 'папки' : 'файла'}`)
        }
    }

    const handleFileUpload = async (e) => {
        try {
            const file = e.target.files?.[0]
            if (!file) return
            if (!selectedFolder) {
                createNotification('warning', 'Выберите папку для загрузки файла')
                return
            }
            await dispatch(createFile(file, selectedFolder.mrid))
            createNotification('success', 'Файл успешно загружен')
            e.target.value = ''
        } catch (err) {
            createNotification('error', 'Ошибка загрузки файла')
        }
    }

    const createFileAt = () => {
        if (!selectedFolder) {
            createNotification('warning', 'Выберите папку для загрузки файла')
            return
        }
        fileInputRef.current?.click()
    }

    const getFileIcon = (fileName) => {
        const ext = fileName.split('.').pop()?.toLowerCase()
        const iconMap = {
            'pdf': '📕', 'doc': '📘', 'docx': '📘',
            'xls': '📗', 'xlsx': '📗', 'ppt': '📙', 'pptx': '📙',
            'mp4': '🎬', 'avi': '🎬', 'mov': '🎬',
            'mp3': '🎵', 'wav': '🎵', 'ogg': '🎵',
            'jpg': '🖼️', 'jpeg': '🖼️', 'png': '🖼️', 'gif': '🖼️', 'webp': '🖼️',
            'zip': '📦', 'rar': '📦', '7z': '📦',
            'txt': '📄', 'html': '🌐', 'htm': '🌐', 'css': '🎨', 'js': '📜', 'json': '📋'
        }
        return iconMap[ext] || '📄'
    }

    const findFolderById = useCallback((id, nodes) => {
        for (const n of nodes) {
            if (n.mrid === id) return n
            if (n.children?.length) {
                const found = findFolderById(id, n.children)
                if (found) return found
            }
        }
        return null
    }, [])

    const getAncestorIds = useCallback((folderId, nodes, ancestors = []) => {
        for (const n of nodes) {
            if (n.mrid === folderId) return ancestors
            if (n.children?.length) {
                const found = getAncestorIds(folderId, n.children, [...ancestors, n.mrid])
                if (found !== null) return found
            }
        }
        return null
    }, [])

    const searchResults = useMemo(() => {
        if (!searchQuery.trim()) return []
        const q = searchQuery.toLowerCase()
        const results = []
        const walkFolders = (nodes) => {
            nodes.forEach(n => {
                if (n.mr_name.toLowerCase().includes(q)) results.push({ ...n, resultType: 'folder' })
                if (n.children?.length) walkFolders(n.children)
            })
        }
        walkFolders(folderTree)
        Object.entries(treeFilesMap).forEach(([folderId, filesList]) => {
            filesList.forEach(f => {
                if (f.mr_name.toLowerCase().includes(q)) {
                    results.push({ ...f, resultType: 'file', parentFolderId: parseInt(folderId) })
                }
            })
        })
        return results
    }, [searchQuery, folderTree, treeFilesMap])

    const handleSearchResultClick = (result) => {
        setSearchQuery('')
        if (result.resultType === 'folder') {
            const ancestors = getAncestorIds(result.mrid, folderTree, []) || []
            setExpandedFolders(prev => {
                const next = new Set(prev)
                ancestors.forEach(id => next.add(id))
                return next
            })
            handleFolderSelect(result)
        } else {
            const parentFolder = findFolderById(result.parentFolderId, folderTree)
            if (parentFolder) {
                const ancestors = getAncestorIds(parentFolder.mrid, folderTree, []) || []
                setExpandedFolders(prev => {
                    const next = new Set(prev)
                    ancestors.forEach(id => next.add(id))
                    next.add(parentFolder.mrid)
                    return next
                })
                handleFolderSelect(parentFolder)
            }
        }
    }

    const renderFolder = (folderItem, level = 0) => {
        const isSelected = selectedFolder?.mrid === folderItem.mrid
        const hasChildren = folderItem.children?.length > 0
        const isExpanded = expandedFolders.has(folderItem.mrid)
        const folderFilesList = treeFilesMap[folderItem.mrid] || []

        return (
            <div key={folderItem.mrid} className={styles.folderItem}>
                <div
                    className={cn(styles.folderRow, { [styles.selected]: isSelected, [styles.nested]: level > 0 })}
                    style={{ paddingLeft: `${level * 20 + 8}px` }}
                    onClick={() => handleFolderSelect(folderItem)}
                    role="treeitem"
                    tabIndex={0}
                    aria-selected={isSelected}
                    aria-expanded={isExpanded}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleFolderSelect(folderItem) }
                        else if (e.key === 'ArrowRight' && !isExpanded) { e.preventDefault(); toggleFolder(folderItem.mrid) }
                        else if (e.key === 'ArrowLeft' && isExpanded) { e.preventDefault(); toggleFolder(folderItem.mrid) }
                    }}
                >
                    <button
                        className={styles.expandButton}
                        onClick={(e) => toggleFolder(folderItem.mrid, e)}
                        aria-label={isExpanded ? 'Свернуть' : 'Развернуть'}
                        title={isExpanded ? 'Свернуть' : 'Развернуть'}
                    >
                        {isExpanded ? '▾' : '▸'}
                    </button>
                    <span className={level > 0 ? styles.folderIconMethodWith : styles.folderIconMethod}></span>
                    <span className={styles.folderName}>{folderItem.mr_name}</span>
                    <button
                        className={styles.dotsButton}
                        onClick={(e) => openContextMenu(e, folderItem, 'folder')}
                        title="Действия"
                        aria-label="Меню действий"
                    >⋮</button>
                </div>

                {isExpanded && (
                    <div className={styles.folderChildren}>
                        {hasChildren && folderItem.children.map(child => renderFolder(child, level + 1))}
                        {folderFilesList.map(file => (
                            <div
                                key={file.mrid}
                                className={cn(styles.treeFileRow, { [styles.treeFileSelected]: selectedFile?.mrid === file.mrid })}
                                style={{ paddingLeft: `${(level + 1) * 20 + 8}px` }}
                                onClick={(e) => { e.stopPropagation(); setSelectedFile(file) }}
                                role="treeitem"
                                tabIndex={0}
                                title={file.mr_name}
                                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setSelectedFile(file) } }}
                            >
                                <span className={styles.expandPlaceholder}></span>
                                <span className={styles.treeFileIcon}>{getFileIcon(file.mr_name)}</span>
                                <span className={styles.treeFileName}>{file.mr_name}</span>
                                <button
                                    className={styles.dotsButton}
                                    onClick={(e) => openContextMenu(e, { ...file, mr_parent: folderItem.mrid }, 'file')}
                                    title="Действия"
                                >⋮</button>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        )
    }

    return (
        <>
            <div className={styles.aboveMet}>
                <h1>Методический кабинет</h1>
            </div>

            <Modal
                closeModal={closeModal}
                data={modalData}
                file={selectedFolder}
                onFolderCreated={refreshFolderTree}
            />

            <MediaPreviewModal
                file={previewFile}
                fileUrl={previewObjectUrl}
                open={!!previewFile}
                onClose={closePreview}
            />

            {deleteConfirm && (
                <div className={styles.dialogOverlay}>
                    <div className={styles.confirmDialog}>
                        <p className={styles.confirmMessage}>
                            {deleteConfirm.type === 'folder'
                                ? `Вы уверены, что хотите удалить «${deleteConfirm.item.mr_name}» и её содержимое? Изменения будет невозможно отменить.`
                                : `Вы уверены, что хотите удалить «${deleteConfirm.item.mr_name}»? Изменения будет невозможно отменить.`
                            }
                        </p>
                        <div className={styles.confirmButtons}>
                            <button className={styles.confirmYes} onClick={confirmDelete}>Да</button>
                            <button className={styles.confirmNo} onClick={() => setDeleteConfirm(null)}>Нет</button>
                        </div>
                    </div>
                </div>
            )}

            {contextMenu && (
                <div
                    ref={contextMenuRef}
                    className={styles.contextMenu}
                    style={{ top: contextMenu.y, left: contextMenu.x }}
                >
                    {contextMenu.type === 'folder' ? (
                        <>
                            <div className={styles.contextMenuItem} onClick={() => { handleFolderSelect(contextMenu.item); closeContextMenu() }}>Открыть</div>
                            <div className={styles.contextMenuItem} onClick={() => { openModal(contextMenu.item); closeContextMenu() }}>Свойства</div>
                            <div className={cn(styles.contextMenuItem, styles.contextMenuDelete)} onClick={() => requestDelete(contextMenu.item, 'folder')}>Удалить</div>
                        </>
                    ) : (
                        <>
                            <div className={styles.contextMenuItem} onClick={() => { setPreviewFile(contextMenu.item); closeContextMenu() }}>Просмотр</div>
                            <div className={styles.contextMenuItem} onClick={() => handleDownloadFile(contextMenu.item)}>Скачать</div>
                            <div className={styles.contextMenuItem} onClick={() => { createNotification('info', 'Свойства файла в разработке'); closeContextMenu() }}>Свойства</div>
                            <div className={cn(styles.contextMenuItem, styles.contextMenuDelete)} onClick={() => requestDelete(contextMenu.item, 'file')}>Удалить</div>
                        </>
                    )}
                </div>
            )}

            <div className={styles.container}>
                {/* Левая панель — дерево папок */}
                <div className={styles.sidebar}>
                    <div className={styles.searchContainer}>
                        <input
                            type="text"
                            className={styles.searchInput}
                            placeholder="Поиск..."
                            value={searchQuery}
                            onChange={e => setSearchQuery(e.target.value)}
                        />
                        {searchQuery && (
                            <button className={styles.searchClear} onClick={() => setSearchQuery('')}>×</button>
                        )}
                    </div>

                    {!searchQuery && folderTree.length > 0 && (
                        <button className={styles.expandAllButton} onClick={isFullyExpanded ? collapseAll : expandAll}>
                            {isFullyExpanded ? 'Свернуть все' : 'Раскрыть все'}
                        </button>
                    )}

                    {searchQuery ? (
                        <div className={styles.searchResults}>
                            {searchResults.length === 0 ? (
                                <div className={styles.emptyState}>Ничего не найдено</div>
                            ) : (
                                searchResults.map((r, i) => (
                                    <div key={i} className={styles.searchResultItem} onClick={() => handleSearchResultClick(r)}>
                                        <span className={styles.searchResultIcon}>
                                            {r.resultType === 'folder' ? '📁' : getFileIcon(r.mr_name)}
                                        </span>
                                        <span className={styles.searchResultName}>{r.mr_name}</span>
                                        <span className={styles.searchResultType}>
                                            {r.resultType === 'folder' ? 'Папка' : 'Файл'}
                                        </span>
                                    </div>
                                ))
                            )}
                        </div>
                    ) : (
                        <div className={styles.folderTree}>
                            {!loading && folderTree.length === 0 && (
                                <div className={styles.emptyState}>Папки не найдены</div>
                            )}
                            {folderTree.map(folderItem => renderFolder(folderItem))}
                        </div>
                    )}
                </div>

                {/* Средняя панель — содержимое */}
                <div className={styles.content}>
                    {selectedFolder && (
                        <div className={styles.navBar}>
                            <button
                                className={cn(styles.navButton, { [styles.navButtonDisabled]: folderHistory.length === 0 })}
                                onClick={handleBack}
                                disabled={folderHistory.length === 0}
                                title="Назад"
                            >
                                ← Назад
                            </button>
                            <button
                                className={styles.navButton}
                                onClick={handleClose}
                                title="Закрыть и перейти в корень"
                            >
                                × Закрыть
                            </button>
                        </div>
                    )}

                    <div className={styles.fileList}>
                        {!loading && selectedFolder && (
                            <>
                                {selectedFolderContent.childFolders.length > 0 && (
                                    <div className={styles.contentSection}>
                                        <h3 className={styles.sectionTitle}>Папки</h3>
                                        {selectedFolderContent.childFolders.map(childFolder => (
                                            <div
                                                key={childFolder.mrid}
                                                className={styles.folderItemContent}
                                                onClick={() => handleFolderSelect(childFolder)}
                                                role="button"
                                                tabIndex={0}
                                                onKeyDown={(e) => {
                                                    if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleFolderSelect(childFolder) }
                                                }}
                                            >
                                                <span className={styles.folderIconContent}>📁</span>
                                                <span className={styles.folderNameContent}>{childFolder.mr_name}</span>
                                                <button
                                                    className={styles.dotsButton}
                                                    onClick={(e) => openContextMenu(e, childFolder, 'folder')}
                                                    title="Действия"
                                                >⋮</button>
                                            </div>
                                        ))}
                                    </div>
                                )}

                                {selectedFolderContent.files.length > 0 && (
                                    <div className={styles.contentSection}>
                                        {selectedFolderContent.childFolders.length > 0 && (
                                            <h3 className={styles.sectionTitle}>Файлы</h3>
                                        )}
                                        {selectedFolderContent.files.map(file => {
                                            const isFileSelected = selectedFile?.mrid === file.mrid
                                            return (
                                                <div
                                                    key={file.mrid}
                                                    className={cn(styles.fileItem, { [styles.selected]: isFileSelected })}
                                                    role="button"
                                                    tabIndex={0}
                                                    onClick={() => setSelectedFile(file)}
                                                    onKeyDown={(e) => {
                                                        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setSelectedFile(file) }
                                                    }}
                                                >
                                                    <span className={styles.fileIcon}>{getFileIcon(file.mr_name)}</span>
                                                    <span className={styles.fileLink} title={file.mr_name}>{file.mr_name}</span>
                                                    <button
                                                        className={styles.fileActionBtn}
                                                        onClick={(e) => { e.stopPropagation(); setPreviewFile(file) }}
                                                        title="Просмотр"
                                                        aria-label="Просмотр"
                                                    >
                                                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                            <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                                                            <circle cx="12" cy="12" r="3"/>
                                                        </svg>
                                                    </button>
                                                    <button
                                                        className={styles.dotsButton}
                                                        onClick={(e) => openContextMenu(e, file, 'file')}
                                                        title="Действия"
                                                    >⋮</button>
                                                </div>
                                            )
                                        })}
                                    </div>
                                )}

                                {selectedFolderContent.childFolders.length === 0 && selectedFolderContent.files.length === 0 && (
                                    <div className={styles.emptyState}>В этой папке нет папок и файлов</div>
                                )}
                            </>
                        )}

                        {!selectedFolder && (
                            <div className={styles.emptyState}>Выберите папку для просмотра содержимого</div>
                        )}
                    </div>
                </div>

                {/* Правая панель — иконки действий */}
                <div className={styles.actionsPanel}>
                    <button
                        className={styles.iconActionBtn}
                        onClick={() => openModal()}
                        title="Создать папку"
                        aria-label="Создать папку"
                    >
                        <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
                            <path d="M20 6h-8l-2-2H4C2.9 4 2 4.9 2 6v12c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V8c0-1.1-.9-2-2-2z" fill="white"/>
                            <line x1="12" y1="10" x2="12" y2="17" stroke="#3689FF" strokeWidth="2.5" strokeLinecap="round"/>
                            <line x1="8.5" y1="13.5" x2="15.5" y2="13.5" stroke="#3689FF" strokeWidth="2.5" strokeLinecap="round"/>
                        </svg>
                    </button>
                    <button
                        className={styles.iconActionBtn}
                        onClick={createFileAt}
                        title="Добавить файл"
                        aria-label="Добавить файл"
                    >
                        <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
                            <path d="M14 2H6C4.9 2 4 2.9 4 4v16c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V8l-6-6z" fill="white"/>
                            <polyline points="14,2 14,8 20,8" stroke="white" fill="none" strokeWidth="1.5"/>
                            <line x1="12" y1="11" x2="12" y2="17" stroke="#3689FF" strokeWidth="2.5" strokeLinecap="round"/>
                            <line x1="9" y1="14" x2="15" y2="14" stroke="#3689FF" strokeWidth="2.5" strokeLinecap="round"/>
                        </svg>
                    </button>
                    <input type='file' ref={fileInputRef} onChange={handleFileUpload} hidden />
                </div>
            </div>

            {selectedFolder && (
                <div className={styles.footer}>
                    <div className={styles.footerLeft}>
                        <span>Создал: {selectedFolder.created_by || 'Не указано'}</span>
                    </div>
                    <div className={styles.footerRight}>
                        <span>Создан(а): {selectedFolder.created_at
                            ? new Date(selectedFolder.created_at).toLocaleString('ru-RU', {
                                day: '2-digit', month: '2-digit', year: 'numeric',
                                hour: '2-digit', minute: '2-digit', second: '2-digit'
                            })
                            : 'Не указано'}
                        </span>
                    </div>
                </div>
            )}
        </>
    )
}
